import { useState } from 'react';
import { Alert, Empty, Flex, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Icon from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import EditIcon from '@/assets/icons/pencil-solid-full.svg?react';
import PhoneIcon from '@/assets/icons/phone-outgoing-01.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { Button } from '@/components/button/button';
import IconButton from '@/components/icon-button/icon-button';
import Toggle from '@/components/toggle/toggle';
import VsTable from '@/components/vs-table/vs-table';
import {
  useOutboundScenarios,
  useUpdateOutboundScenario,
} from '@/hooks/use-outbound-scenarios/use-outbound-scenarios';
import { OutboundScenario } from '@/services/outbound-calls-service/scenarios.service.types';
import { openNotification } from '@/utils/notification/notification';
import { useOutboundContext } from '../outbound-calls.context';
import { outboundErrorMessage } from '../outbound-calls.messages';
import { formatDateTime } from '../outbound-calls.utils';
import { ScenarioFormModal } from './scenario-form-modal';
import { TestCallModal } from './test-call-modal';
import styles from '../outbound-calls.module.scss';

type FormState = { open: false } | { open: true; scenario?: OutboundScenario };

/** No delete in the API: a scenario a campaign used stays, switched off. */
export function OutboundScenarios() {
  const { t } = useTranslation('outbound');
  const { company } = useOutboundContext();
  const { scenarios, isLoading, error } = useOutboundScenarios(company.id);
  const { updateScenario } = useUpdateOutboundScenario();
  const [form, setForm] = useState<FormState>({ open: false });
  const [testCallScenario, setTestCallScenario] = useState<OutboundScenario | undefined>();

  const toggleActive = (scenario: OutboundScenario, active: boolean) =>
    updateScenario({ id: scenario.id, payload: { active } }).catch((toggleError: unknown) =>
      openNotification({
        type: 'error',
        message: outboundErrorMessage(toggleError, t('actionFailed')),
      })
    );

  const columns: ColumnsType<OutboundScenario> = [
    {
      title: t('scenarios.columns.name'),
      key: 'name',
      render: (_, scenario) => <b>{scenario.name}</b>,
    },
    {
      title: t('scenarios.columns.objective'),
      key: 'objective',
      render: (_, scenario) => (
        <span className={`${styles.secondary} ${styles.clamp}`}>{scenario.objective}</span>
      ),
    },
    {
      title: t('scenarios.columns.active'),
      key: 'active',
      render: (_, scenario) => (
        <Toggle
          checked={scenario.active}
          label={t('scenarios.toggleActive', { name: scenario.name })}
          onChange={(checked) => toggleActive(scenario, checked)}
        />
      ),
    },
    {
      title: t('scenarios.columns.updatedAt'),
      key: 'updated_at',
      render: (_, scenario) => formatDateTime(scenario.updated_at),
    },
    {
      key: 'actions',
      align: 'right',
      render: (_, scenario) => (
        <Flex gap={8} justify="flex-end">
          <Tooltip title={scenario.active ? undefined : t('scenarios.testCallInactive')}>
            <span>
              <IconButton
                label={t('scenarios.testCall', { name: scenario.name })}
                className={styles.strokeIcon}
                disabled={!scenario.active}
                onClick={() => setTestCallScenario(scenario)}
              >
                <PhoneIcon />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            label={t('scenarios.edit', { name: scenario.name })}
            onClick={() => setForm({ open: true, scenario })}
          >
            <EditIcon />
          </IconButton>
        </Flex>
      ),
    },
  ];

  return (
    <Flex vertical gap={14}>
      <div className={styles.toolbar}>
        <span />
        <Button buttonVariant="primary" onClick={() => setForm({ open: true })}>
          <Icon component={() => <PlusIcon className={styles.buttonIcon} />} />
          {t('scenarios.add')}
        </Button>
      </div>

      {error ? (
        <Alert type="error" showIcon message={outboundErrorMessage(error, t('errors.load'))} />
      ) : (
        <VsTable<OutboundScenario>
          rowKey="id"
          loading={isLoading}
          dataSource={scenarios}
          columns={columns}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('scenarios.empty')} />
            ),
          }}
        />
      )}

      <ScenarioFormModal
        open={form.open}
        companyId={company.id}
        scenario={form.open ? form.scenario : undefined}
        onClose={() => setForm({ open: false })}
      />
      <TestCallModal scenario={testCallScenario} onClose={() => setTestCallScenario(undefined)} />
    </Flex>
  );
}
